import React, { useState, useMemo } from 'react';
import { Availability, AvailabilityData } from '../hooks/useAvailability';

const DAYS_NL = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

interface CalendarProps {
  availability: Availability;
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  multiSelect?: boolean;
  selectedDates?: string[];
  onMultiSelect?: (dates: string[]) => void;
  onMonthChange?: (year: number, month: number) => void;
}

export default function Calendar({ availability, selectedDate, onDateSelect, multiSelect = false, selectedDates = [], onMultiSelect, onMonthChange }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Report current month to parent on mount and changes
  React.useEffect(() => {
    if (onMonthChange) {
      onMonthChange(currentMonth.getFullYear(), currentMonth.getMonth());
    }
  }, [currentMonth, onMonthChange]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    
    // Day of week of first day (0 = Sunday)
    const startDayOfWeek = firstDay.getDay();
    // Adjust for Monday start (0 = Monday)
    const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStart - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Add days from next month to complete grid (6 rows)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentMonth]);

  const formatDateString = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = formatDateString(new Date());

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    onDateSelect(today);
  };

  const handleDayClick = (dateStr: string, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return;
    
    if (multiSelect && onMultiSelect) {
      if (selectedDates.includes(dateStr)) {
        onMultiSelect(selectedDates.filter(d => d !== dateStr));
      } else {
        onMultiSelect([...selectedDates, dateStr]);
      }
    } else {
      onDateSelect(dateStr);
    }
  };

  const getDayClass = (date: Date, isCurrentMonth: boolean): string => {
    const dateStr = formatDateString(date);
    const avail = availability[dateStr];
    
    let classes = ['calendar-day'];
    
    if (!isCurrentMonth) classes.push('other-month');
    if (dateStr === today) classes.push('today');
    if (multiSelect && selectedDates.includes(dateStr)) classes.push('multi-selected');
    else if (dateStr === selectedDate) classes.push('selected');
    
    if (avail && isCurrentMonth) {
      if (avail.status === 'available') classes.push('available');
      else if (avail.status === 'partial') classes.push('partial');
      else if (avail.status === 'unavailable') classes.push('unavailable');
    }
    
    return classes.join(' ');
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2>{MONTHS_NL[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
        <div className="calendar-nav">
          <button onClick={prevMonth}>◀</button>
          <button onClick={goToToday} style={{ fontSize: '0.9rem' }}>Vandaag</button>
          <button onClick={nextMonth}>▶</button>
        </div>
      </div>
      
      <div className="calendar-grid">
        {/* Day headers - start with Monday */}
        {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(day => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        
        {/* Calendar days */}
        {calendarDays.map(({ date, isCurrentMonth }, index) => {
          const dateStr = formatDateString(date);
          return (
            <div
              key={index}
              className={getDayClass(date, isCurrentMonth)}
              onClick={() => handleDayClick(dateStr, isCurrentMonth)}
            >
              {date.getDate()}
              {multiSelect && selectedDates.includes(dateStr) && (
                <span className="multi-check">✓</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
